// src/pages/CtcScreeningListPage.js
// Route: /ctc-screenings — ADMIN / HEALTH_NAVIGATOR (backend-enforced).
//
// CTC camp screening worklist. Mirrors PatientListPage.js's structure: page
// header, stat cards, debounced search + status toolbar, table, manual
// page-number pagination.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { ctcScreeningAPI } from '../services/api';
import Icon from '../components/Icon';
import OverflowMenu from '../components/OverflowMenu';
import avatarTint from '../utils/avatarTint';
import { SCREENING_STATUSES, CASE_FLAGS, statusMeta, formatDate, ageLabel } from '../utils/ctcScreening';

const BREADCRUMBS = [{ label: 'CTC Screenings' }];
const PAGE_SIZE = 10;
const COLUMN_COUNT = 9;

// Phone layout (max-width 768px) — see the "CTC Screenings list — phone
// layout" block in index.css. Everything tagged mlist-* / ctc-mlist-* is
// either restyled there or shown on only one side of the breakpoint
// (mlist-phone-only / mlist-desktop-only); the desktop table is untouched.
const sentenceCase = (s = '') => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ');
// Name words with empty and missing parts dropped, so a one-word name gives
// one initial and a null last name never reads as "null".
const nameWords = (s) => [s.firstName, s.lastName]
  .filter(part => typeof part === 'string')
  .join(' ')
  .split(/\s+/)
  .filter(Boolean);
const initials = (words) => (words.length > 1 ? words[0][0] + words[words.length - 1][0] : words[0]?.[0] || '?').toUpperCase();
// Desktop has an "Age / DOB" column for context; the phone line labels a bare date.
const phoneAge = (s) => (s.ageAtScreening != null ? `${s.ageAtScreening} yrs` : s.dateOfBirth ? `DOB ${formatDate(s.dateOfBirth)}` : null);

export default function CtcScreeningListPage() {
  const navigate = useNavigate();
  const [screenings, setScreenings] = useState([]);
  const [stats, setStats]           = useState({});
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatus]   = useState('ALL');
  const [page, setPage]             = useState(1);
  const [totalPages, setTotal]      = useState(1);
  const [totalCount, setCount]      = useState(0);

  const fetchScreenings = useCallback(async () => {
    setLoading(true);
    try {
      const paging = { page: page - 1, size: PAGE_SIZE };
      // GET /ctc-screenings/search takes no status param, so a search always
      // spans every status — the status buttons are disabled while one is active.
      const res = search
        ? await ctcScreeningAPI.search(search, paging)
        : await ctcScreeningAPI.list({ ...paging, ...(statusFilter !== 'ALL' && { status: statusFilter }) });
      setScreenings(res.data?.screenings || []);
      setTotal(res.data?.totalPages || 1);
      setCount(res.data?.totalElements || 0);
    } catch {
      setScreenings([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchScreenings, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchScreenings, search]);

  useEffect(() => {
    ctcScreeningAPI.stats().then(r => setStats(r.data || {})).catch(() => {});
  }, []);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Pagination buttons are divs (kept, so desktop renders as before); these
  // give them a role, a tab stop and Enter/Space like a real button.
  const pageBtnProps = (label, enabled, go) => ({
    role: 'button',
    'aria-label': label,
    'aria-disabled': !enabled || undefined,
    tabIndex: enabled ? 0 : -1,
    onKeyDown: (e) => {
      if (enabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); go(); }
    },
  });

  return (
    <AppShell breadcrumbs={BREADCRUMBS}>
      <div className="page-header mlist-header ctc-mlist-header fade-in-up">
        <div className="mlist-header-text">
          <div className="page-title">CTC Screenings</div>
          <div className="page-subtitle mlist-desktop-only">People screened at CTC camps — managed by navigators, no portal login</div>
          {(!loading || totalCount > 0) && (
            <div className="mlist-count mlist-phone-only">{totalCount} screened</div>
          )}
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary mlist-desktop-only" onClick={() => navigate('/ctc-screenings/import')}>
            <Icon name="importInto" size={15} /> Import Screenings
          </button>
          <OverflowMenu
            className="mlist-phone-only"
            label="More screening actions"
            items={[
              { label: 'Import Screenings', onSelect: () => navigate('/ctc-screenings/import') },
            ]}
          />
          <button className="btn btn-primary mlist-primary" onClick={() => navigate('/ctc-screenings/new')}>
            <span className="mlist-desktop-only">+ Add Screening</span>
            <span className="mlist-phone-only mlist-btn-label"><Icon name="plus" size={16} /> Add screening</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      {/* One bordered strip on phones (mlist-stats), four cards on desktop. */}
      <div className="stats-row mlist-stats fade-in-up delay-1">
        <div className="stat-card">
          <div className="stat-label">Total Screened</div>
          <div className="stat-value">{stats.totalScreened ?? '—'}</div>
          <div className="stat-change">all camps</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Open Cases</div>
          <div className="stat-value" style={stats.openCases > 0 ? { color: 'var(--amber-text)' } : undefined}>{stats.openCases ?? '—'}</div>
          <div className="stat-change">follow-up needed</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">In Treatment</div>
          <div className="stat-value">{stats.inTreatment ?? '—'}</div>
          <div className="stat-change">currently being treated</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Converted</div>
          <div className="stat-value">{stats.converted ?? '—'}</div>
          <div className="stat-change">now CTC patients</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar mlist-toolbar fade-in-up delay-2">
        <div className="search-input-wrap">
          <label className="mlist-sr-only" htmlFor="ctc-search">Search screenings</label>
          <span className="search-icon"><Icon name="search" size={15} /></span>
          <input
            id="ctc-search"
            className="search-input"
            placeholder="Search by name or phone…"
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        {/* display: contents on desktop, so the chips stay toolbar items
            there; on phones this is their own horizontally scrolling line. */}
        <div className="mlist-chips" role="group" aria-label="Filter by status">
          {[{ value: 'ALL', label: 'All Status' }, ...SCREENING_STATUSES].map(s => {
            const pressed = statusFilter === s.value && !search;
            return (
              <button
                key={s.value}
                className={`btn btn-sm mlist-chip ${pressed ? 'btn-primary' : 'btn-ghost'}`}
                aria-pressed={pressed}
                disabled={Boolean(search)}
                title={search ? 'Search covers every status — clear it to filter by status' : undefined}
                onClick={() => { setStatus(s.value); setPage(1); }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      {/* ctc-mlist scopes this page's phone rules in index.css. */}
      <div className="table-wrap ctc-mlist mlist-rows fade-in-up delay-3">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Age / DOB</th>
              <th>Phone</th>
              <th>District</th>
              <th>Camp Date</th>
              <th>Overall Result</th>
              <th>Case Flags</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLUMN_COUNT}>
                  <div className="loading-state">
                    <div className="spinner" />
                    Loading screenings…
                  </div>
                </td>
              </tr>
            ) : screenings.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT}>
                  <div className="empty-state">
                    <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="clipboard" size={20} /></div>
                    <div className="empty-title">No screenings found</div>
                    <div className="empty-desc">
                      {search || statusFilter !== 'ALL'
                        ? 'Try adjusting your search or filter'
                        : 'Add a screening or import a camp sheet to get started'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : screenings.map((s) => {
              const status = statusMeta(s.status);
              const flags = CASE_FLAGS.filter(f => s[f.key]);
              const words = nameWords(s);
              const name = words.join(' ');
              // Phones: result, then open flags, then in-treatment flags.
              const caseLine = [
                s.overallResult,
                ...flags.filter(f => !f.treatment).map(f => f.phone),
                ...flags.filter(f => f.treatment).map(f => f.phone),
              ].filter(Boolean).join(' · ');
              const detailLine = [s.gender && sentenceCase(s.gender), phoneAge(s), s.phone, s.campName].filter(Boolean).join(' · ');
              return (
                <tr key={s.id} className={`mlist-row${caseLine ? ' ctc-mlist-has-cases' : ''}`}>
                  <td className="cell-stack mlist-c-main">
                    <span className={`avatar mlist-avatar mlist-phone-only ${avatarTint(name, 4)}`} aria-hidden="true">
                      {initials(words)}
                    </span>
                    <div className="mlist-name">{[s.firstName, s.lastName].filter(Boolean).join(' ')}</div>
                    <div className="cell-sub mlist-sub">
                      <span className="mlist-desktop-only">{s.gender || 'Gender not recorded'}{s.campName ? ` · ${s.campName}` : ''}</span>
                      <span className="mlist-phone-only" title={detailLine || undefined}>{detailLine}</span>
                    </div>
                    {caseLine && (
                      <div className="ctc-mlist-cases mlist-phone-only" title={caseLine}>{caseLine}</div>
                    )}
                  </td>
                  <td>{ageLabel(s) || '—'}</td>
                  <td>{s.phone || '—'}</td>
                  <td>{s.district || '—'}</td>
                  <td>{formatDate(s.registeredDate) || '—'}</td>
                  <td>
                    {/* Free text on the backend — no agreed vocabulary yet, so
                        no colour is implied; badge-prose guards long values. */}
                    {s.overallResult
                      ? <span className="badge badge-unknown badge-prose">{s.overallResult}</span>
                      : '—'}
                  </td>
                  <td>
                    {flags.length === 0 ? '—' : (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {flags.map(f => (
                          <span key={f.key} className={`badge ${f.badge}`} title={f.label}>{f.short}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="mlist-c-status"><span className={`badge ${status.badge}`}>{status.label}</span></td>
                  <td className="mlist-c-end">
                    <button
                      className="btn btn-secondary btn-sm mlist-desktop-only"
                      onClick={() => navigate(`/ctc-screenings/${s.id}`)}
                    >
                      View →
                    </button>
                    <span className="mlist-chevron mlist-phone-only" aria-hidden="true"><Icon name="chevronRight" size={18} /></span>
                    {/* Phones: stretched over the row, so the whole row opens the record. */}
                    <Link className="ctc-mlist-link mlist-phone-only" to={`/ctc-screenings/${s.id}`}
                      data-testid="ctc-row-link"
                      aria-label={`Open ${name || 'screening'}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="table-footer mlist-footer">
          <span>
            {loading
              ? 'Loading…'
              : `Showing ${screenings.length ? (page - 1) * PAGE_SIZE + 1 : 0}–${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount} screenings`
            }
          </span>
          <div className="pagination">
            <div className={`page-btn ${page === 1 ? 'disabled' : ''}`}
              {...pageBtnProps('Previous page', page > 1, () => setPage(p => p - 1))}
              onClick={() => page > 1 && setPage(p => p - 1)}
              style={{ cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}
            >‹</div>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(n => (
              <div
                key={n}
                className={`page-btn mlist-page-num ${page === n ? 'active' : ''}`}
                {...pageBtnProps(`Page ${n}`, true, () => setPage(n))}
                aria-current={page === n ? 'page' : undefined}
                onClick={() => setPage(n)}
              >{n}</div>
            ))}
            {/* Past page 5 there is no numbered button for the current page;
                phones show "‹ current ›", so give them one. */}
            {page > 5 && (
              <div className="page-btn active mlist-phone-only" aria-current="page">{page}</div>
            )}
            {totalPages > 5 && <div className="page-btn mlist-page-gap" style={{ cursor: 'default' }} aria-hidden="true">…</div>}
            <div
              className="page-btn"
              {...pageBtnProps('Next page', page < totalPages, () => setPage(p => p + 1))}
              onClick={() => page < totalPages && setPage(p => p + 1)}
              style={{ cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}
            >›</div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
